/**
 * Formatação e leitura de campos do financeiro. Datas de vencimento são `AAAA-MM-DD` (calendário,
 * sem fuso); "hoje" é o dia em São Paulo, não o do navegador nem o do servidor.
 */
import { addDays, addMonths, diffDays } from "../../../../packages/financial/src/cashflow.js";

export { addDays, addMonths, diffDays };

/** `AAAA-MM-DD` → `dd/mm/aaaa`. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

/** Dia corrente em São Paulo, `AAAA-MM-DD`. */
export function todayIso(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/**
 * Lê um valor digitado em reais ("1.234,56", "1234.5", "R$ 99") e devolve centavos inteiros.
 * `null` para vazio, zero, negativo ou texto que não é número — a tela mostra o erro, nunca grava lixo.
 */
export function brlToCents(input: string): number | null {
  const cleaned = input.replace(/[^\d,.-]/g, "").trim();
  if (!cleaned || cleaned.startsWith("-")) return null;
  let normalized = cleaned;
  if (cleaned.includes(",")) normalized = cleaned.replace(/\./g, "").replace(",", ".");
  else if ((cleaned.match(/\./g) ?? []).length > 1) normalized = cleaned.replace(/\./g, "");
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  const cents = Math.round(value * 100);
  return cents > 0 ? cents : null;
}

/** Aceita zero e negativo (saldo inicial pode ser devedor). */
export function signedBrlToCents(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const negative = trimmed.startsWith("-");
  const cents = brlToCents(negative ? trimmed.slice(1) : trimmed);
  if (cents === null) return trimmed.replace(/[^\d]/g, "") && Number(trimmed.replace(/[^\d]/g, "")) === 0 ? 0 : null;
  return negative ? -cents : cents;
}

export function centsToInput(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export type DueSituation = { kind: "overdue"; days: number } | { kind: "today" } | { kind: "upcoming"; days: number };

/** Quantos dias faltam (ou passaram) para o vencimento. */
export function dueSituation(dueDate: string, today: string): DueSituation {
  const days = diffDays(today, dueDate);
  if (days < 0) return { kind: "overdue", days: -days };
  if (days === 0) return { kind: "today" };
  return { kind: "upcoming", days };
}

export function describeDue(situation: DueSituation): string {
  if (situation.kind === "overdue") return situation.days === 1 ? "venceu ontem" : `venceu há ${situation.days} dias`;
  if (situation.kind === "today") return "vence hoje";
  return situation.days === 1 ? "vence amanhã" : `vence em ${situation.days} dias`;
}
